import React from "react";
import Typography from "aldoo-ra/Typography";
import Input from "aldoo-ra/Input";
import Checkbox from "aldoo-ra/Checkbox";
import FieldSelector from "./components/FieldSelector";

/**
 * Replace Text Plugin
 * Find and replace text in selected fields
 */
export default {
  id: "cms-replace-text",
  name: "Replace Text",
  description: "Find and replace text in fields",
  category: "field-operations",
  hidden: true,

  requiredCapabilities: {
    needsContentType: true,
    needsSelection: true,
    needsFieldAccess: true,
    needsTypeModification: false,
  },

  validate(context) {
    const errors = [];

    if (!context.contentType) {
      errors.push({ message: "Content type is required" });
    }

    if (!context.selectedItems || context.selectedItems.length === 0) {
      errors.push({ message: "Please select items to modify" });
    }

    return {
      valid: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined,
    };
  },

  getInterface(context) {
    const textFields = context.typeUtils.getAllFields().filter(
      (f) => f.type === "text" || f.type === "longtext"
    );

    return {
      dialog: {
        title: "Replace Text in Fields",
        fields: [
          {
            id: "fields",
            type: "custom",
            label: "Select Fields",
            required: true,
            render: (props) => (
              <FieldSelector
                value={props.value}
                onChange={props.onChange}
                fields={textFields}
                multiple={true}
                label="Select Fields"
                required={true}
                error={props.error}
              />
            ),
          },
          {
            id: "findText",
            type: "custom",
            label: "Find",
            required: true,
            render: (props) => (
              <div>
                <Typography variant="admin_label" className="mb-1">
                  Find
                  <span className="text-admin_error ml-1">*</span>
                </Typography>
                <Input
                  value={props.value || ''}
                  onChange={(e) => props.onChange(e.target.value)}
                  placeholder="Text to find"
                  variant="admin"
                  className="w-full"
                />
                {props.error && (
                  <Typography variant="admin_caption" className="text-admin_error mt-1">
                    {props.error}
                  </Typography>
                )}
              </div>
            ),
          },
          {
            id: "replaceText",
            type: "text",
            label: "Replace With",
            placeholder: "Replacement text (empty to remove)",
            defaultValue: "",
          },
          {
            id: "caseSensitive",
            type: "checkbox",
            label: "Case Sensitive",
            defaultValue: false,
          },
          {
            id: "useRegex",
            type: "checkbox",
            label: "Use Regular Expression",
            defaultValue: false,
          },
        ],

        preview: {
          render: (values, context) => {
            if (!values.findText || !values.fields || values.fields.length === 0) {
              return (
                <Typography variant="admin_caption" className="text-admin_muted">
                  Configure search to see preview
                </Typography>
              );
            }

            // Find matching items
            const matches = [];
            context.selectedItems.forEach((item) => {
              values.fields.forEach((fieldName) => {
                const fieldValue = item.content?.[fieldName];
                if (typeof fieldValue === "string") {
                  const regex = values.useRegex
                    ? new RegExp(values.findText, values.caseSensitive ? "g" : "gi")
                    : new RegExp(
                        values.findText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
                        values.caseSensitive ? "g" : "gi"
                      );

                  if (regex.test(fieldValue)) {
                    matches.push({
                      itemId: item._id,
                      itemName: item.name || item._id,
                      fieldName,
                      oldValue: fieldValue,
                      newValue: fieldValue.replace(regex, values.replaceText || ""),
                    });
                  }
                }
              });
            });

            if (matches.length === 0) {
              return (
                <Typography variant="admin_body" className="text-admin_muted">
                  No matches found
                </Typography>
              );
            }

            return (
              <div className="max-h-60 overflow-y-auto">
                <Typography variant="admin_body" className="mb-2">
                  Found {matches.length} match{matches.length !== 1 ? "es" : ""} in{" "}
                  {new Set(matches.map((m) => m.itemId)).size} item(s)
                </Typography>
                {matches.slice(0, 5).map((match, index) => (
                  <div key={index} className="mb-3 p-2 bg-admin_background rounded text-sm">
                    <Typography variant="admin_caption" className="text-admin_muted mb-1">
                      {match.itemName} → {match.fieldName}
                    </Typography>
                    <div className="flex items-start gap-2">
                      <span className="text-admin_error line-through">
                        {match.oldValue.substring(0, 50)}
                        {match.oldValue.length > 50 ? "..." : ""}
                      </span>
                      <span>→</span>
                      <span className="text-admin_success">
                        {match.newValue.substring(0, 50)}
                        {match.newValue.length > 50 ? "..." : ""}
                      </span>
                    </div>
                  </div>
                ))}
                {matches.length > 5 && (
                  <Typography variant="admin_caption" className="text-admin_muted">
                    ... and {matches.length - 5} more
                  </Typography>
                )}
              </div>
            );
          },
        },
      },

      summary: {
        beforeExecute: (values, context) => ({
          itemsAffected: context.selectedItems.length,
          fieldsAffected: values.fields,
          changes: [
            {
              description: `Replace "${values.findText}" with "${values.replaceText || '(empty)'}" in ${
                values.fields.length
              } field(s)`,
              impact: "medium",
            },
          ],
        }),

        afterExecute: (result) => {
          if (result.success) {
            return `Successfully processed ${result.itemsSucceeded} item(s), ${result.data?.totalReplacements || 0} replacement(s) made`;
          }
          return `Processed ${result.itemsSucceeded} item(s), ${result.itemsFailed} failed`;
        },
      },
    };
  },

  async execute(context, params) {
    const { values } = params;
    const { fields, findText, replaceText, caseSensitive, useRegex } = values;

    let itemsSucceeded = 0;
    let itemsFailed = 0;
    let totalReplacements = 0;
    const errors = [];

    try {
      const regex = useRegex
        ? new RegExp(findText, caseSensitive ? "g" : "gi")
        : new RegExp(
            findText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
            caseSensitive ? "g" : "gi"
          );

      context.ui.showProgress(0, context.selectedItems.length, "Replacing text...");

      for (let i = 0; i < context.selectedItems.length; i++) {
        const item = context.selectedItems[i];

        try {
          const updatedContent = { ...item.content };
          let itemModified = false;

          fields.forEach((fieldName) => {
            const fieldValue = updatedContent[fieldName];
            if (typeof fieldValue === "string" && regex.test(fieldValue)) {
              updatedContent[fieldName] = fieldValue.replace(regex, replaceText || "");
              itemModified = true;
              totalReplacements++;
            }
          });

          if (itemModified) {
            const result = await context.controller.saveContent({
              _id: item._id,
              content: updatedContent,
            }, false);

            if (result?.error) {
              itemsFailed++;
              errors.push({
                itemId: item._id,
                error: result.error,
              });
              // Continue to next item on error
              context.ui.showProgress(i + 1, context.selectedItems.length, "Replacing text...");
              continue;
            }
          }

          itemsSucceeded++;
        } catch (error) {
          itemsFailed++;
          errors.push({
            itemId: item._id,
            error: error.message,
          });
        }

        context.ui.showProgress(i + 1, context.selectedItems.length, "Replacing text...");
      }

      return {
        success: itemsFailed === 0,
        itemsProcessed: context.selectedItems.length,
        itemsSucceeded,
        itemsFailed,
        errors: errors.length > 0 ? errors : undefined,
        data: { totalReplacements },
      };
    } catch (error) {
      return {
        success: false,
        itemsProcessed: 0,
        itemsSucceeded: 0,
        itemsFailed: context.selectedItems.length,
        errors: [{ error: error.message }],
      };
    }
  },
};
